import { useCallback } from 'react';
import { useAuth } from '../auth/AuthProvider.js';
import { clearSessionCookie } from '../auth/sessionCookieTransport.js';
import { useWs } from './WsProvider.js';

/**
 * Composes `AuthProvider` (session state) + `WsProvider` (the live socket)
 * for the one place both are needed together: telling the server
 * (`auth.logout`, best-effort — a network drop shouldn't block signing out
 * locally), clearing the httpOnly refresh cookie (`GET /ws?logout=1`, also
 * best-effort — `auth.logout` already revoked the session DB-side, so a
 * dropped cookie-clear only leaves a dead cookie a later silent-refresh
 * attempt would clear anyway), then clearing the local session, which
 * `WsProvider`'s effect reacts to by disconnecting + wiping persisted WS
 * state.
 */
export function useLogout(): () => Promise<void> {
  const { clearSession } = useAuth();
  const { wsClient, connectionState } = useWs();

  return useCallback(async () => {
    if (connectionState === 'connected') {
      try {
        await wsClient.request('auth.logout');
      } catch {
        // Best-effort — the user is signing out regardless; the server
        // will reap the session on refresh-token expiry if this drops.
      }
    }
    await clearSessionCookie();
    clearSession();
  }, [wsClient, connectionState, clearSession]);
}
